import { FC, KeyboardEvent, useMemo } from 'react';

import { USERS_FILTER_TYPES, USERS_FILTER_TYPES_INT } from 'utils/constants/user';

import {
    Button,
    createStaticDataProvider,
    Flex,
    Input,
    NumberInput,
    Select,
    StaticDataFetcherItem,
} from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';

import { fetchUsers } from 'components/Admin/Users/api/fetchUsers';

import { UserFilterType } from 'store/models/user';
import { setFilterType, setFilterValue } from 'store/models/users';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    filterValue: 'admin.Users.Filter.FilterValue',
    find: 'admin.Users.Filter.find',
    filterType: (value: UserFilterType) => `admin.Users.Filter.FilterType.${value}`,
};

export const UsersFilter: FC = () => {
    const { filterType, filterValue, isLoad } = useSelector((state) => state.users);

    const dispatch = useDispatch();
    const trl = useTrls();

    const isIntFilter = USERS_FILTER_TYPES_INT.includes(filterType);

    const dataProvider = useMemo(
        () =>
            createStaticDataProvider(
                USERS_FILTER_TYPES.map((value) => ({
                    value,
                    text: trl(TrlKeys.filterType(value)),
                }))
            ),
        [trl]
    );

    const selectValue = useMemo(
        () => ({
            value: filterType,
            text: trl(TrlKeys.filterType(filterType)),
        }),
        [filterType, trl]
    );

    const handleFilterTypeChange = (item: StaticDataFetcherItem) => {
        if (item?.value) {
            dispatch(setFilterType(item.value as UserFilterType));
        }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            void dispatch(fetchUsers());
        }
    };

    return (
        <Flex gap={8} align="center">
            <Select
                type="radio"
                value={selectValue}
                dataProvider={dataProvider}
                onChange={handleFilterTypeChange}
                triggerProps={{
                    maxWidth: 200,
                    size: 'medium',
                    stretched: true,
                    'data-qa': 'select-search-by',
                }}
            />
            {isIntFilter ? (
                <NumberInput
                    value={filterValue}
                    onChange={(value) => dispatch(setFilterValue(value))}
                    placeholder={trl(TrlKeys.filterValue)}
                    onKeyDown={handleKeyDown}
                    allowNegative={false}
                    decimalMark={[]}
                    decimalLength={0}
                    groupSize={Infinity}
                    clearable
                    data-qa="search-field"
                />
            ) : (
                <Input
                    value={filterValue}
                    onChange={(value) => dispatch(setFilterValue(value))}
                    placeholder={trl(TrlKeys.filterValue)}
                    onKeyDown={handleKeyDown}
                    clearable
                    data-qa="search-field"
                />
            )}
            <Button
                mode="primary"
                style="accent"
                onClick={() => {
                    void dispatch(fetchUsers());
                }}
                loading={isLoad}
                data-qa="search-button"
            >
                {trl(TrlKeys.find)}
            </Button>
        </Flex>
    );
};
